import React, { memo, useContext } from "react"

import { z } from "zod"
import axios from "axios"
import { parseCookies } from "nookies"
import { useForm } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import { useMutation } from "@tanstack/react-query"
import { useNavigate, useParams, useSearchParams } from "react-router-dom"

import { DictType } from "@pma/types"

import { toast } from "@pma/hooks/use-toast"
import useTranslator from "@pma/hooks/use-translator"

import { cn } from "@pma/utils/helpers"
import { ACCESS_TOKEN } from "@pma/utils/constants"
import { CREATE_TASK_ENDPOINT, SERVER_URL } from "@pma/utils/api-routes"

import { Input } from "@pma/components/common/input"
import { Button } from "@pma/components/common/button"
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@pma/components/common/form"
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@components/common/dialog"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@components/common/select"
import { GroupedTasksContext } from "./context/GroupedTasksProvider"
import { useWarnings } from "@pma/contexts/WarningsContext"

interface Task {
  id: number
  tasks_group_id: number | null
  title?: string
  created_at?: string | number | Date
  _subtasks?: Subtask[]
  [key: string]: any
}

interface Subtask {
  id: number
  parent_tasks_id: number
  [key: string]: any
}

interface Group {
  id: number
  name: string
  project_id: number
  user_id: number
  _tasks: Task[]
  created_at: string | null
}

interface NewTaskProps {
  taskModal: boolean
  setTaskModal: React.Dispatch<React.SetStateAction<boolean>>
  handleAccordion: (groupId: string) => void
}

const formSchema = z.object({
  title: z
    .string()
    .trim()
    .nonempty({ message: "New task is required" })
    .min(2, { message: "Task must be at least 2 characters long" })
    .max(250, { message: "Task can only be up to 250 characters long" }),
  tasks_group_id: z.string().trim().nonempty({ message: "Please select a group for the task" }),
})

const NewTaskComponent = ({ taskModal, setTaskModal, handleAccordion }: NewTaskProps) => {
  const currentTime = Date.now()
  const { refetchWarnings } = useWarnings()
  const midnightTime = new Date().setHours(23, 59, 59, 999);

  const navigate = useNavigate()

  const [searchParam] = useSearchParams()

  const paramProjectId = useParams()?.projectId

  const newParams = new URLSearchParams(searchParam)

  const { translate, locale } = useTranslator()

  const isHebrew = locale === "he"
  const accessToken = parseCookies()[ACCESS_TOKEN]

  const { groupedTasks, setGroupedTasks, setLastInsertTaskId } = useContext(
    GroupedTasksContext,
  ) as {
    groupedTasks: Group[]
    setGroupedTasks: (group: Group[]) => void
    setLastInsertTaskId: (id: number) => void
  }

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      title: "",
      tasks_group_id: "",
    },
  })

  const {
    control,
    reset,
    watch,
    handleSubmit,
    formState: { errors },
  } = form

  // handle reset state
  const handleResetState = () => {
    reset()
    setTaskModal(!taskModal)
  }

  // Create New Task
  const addTask = async (payload: DictType) => {
    const response = await axios.post(
      SERVER_URL + CREATE_TASK_ENDPOINT,
      {
        ...payload,
        start_at: currentTime,
        finish_to: midnightTime,
        project_id: paramProjectId,
      },
      { headers: { Authorization: `Bearer ${accessToken}` } },
    )
    return response.data
  }

  // Create Add Task Mutation
  const addTaskMutation = useMutation({
    mutationKey: ["add-task"],
    mutationFn: addTask,
    onSuccess: (data) => {
      refetchWarnings()
      // Revalidate tasks when tasks are updated
      toast({
        title: translate("Success"),
        description: translate("Task added successfully"),
        variant: "success",
      })

      handleAccordion(watch("tasks_group_id"))
      newParams.set("revalidate", "tasks")
      newParams.delete("user")
      newParams.delete("status")
      newParams.delete("from")
      newParams.delete("to")
      newParams.delete("tracker")
      newParams.delete("query")

      const newGroupedTasks = groupedTasks.map((group) => {
        if (group?.id === Number(watch("tasks_group_id"))) group._tasks = [data, ...group._tasks]
        return group
      })
      setGroupedTasks(newGroupedTasks)
      setLastInsertTaskId(data.id)

      navigate(`?${newParams.toString()}`)
      handleResetState()
    },
    onError: (error: DictType) => {
      const errorMessage = error?.response?.data?.message
      toast({
        title: translate("Error"),
        description: translate(errorMessage || "Error adding task"),
        variant: "error",
      })
    },
  })

  // handle submit Task details
  const onSubmit = (data: z.infer<typeof formSchema>) => {
    addTaskMutation.mutate({
      ...data,
      tasks_group_id: data.tasks_group_id === String(-1) ? 0 : data.tasks_group_id,
    })
  }

  return (
    <Dialog open={taskModal} onOpenChange={handleResetState}>
      <DialogContent className={cn("z-50 w-5/6 md:w-full", isHebrew ? "rtl" : "ltr")}>
        <DialogHeader>
          <DialogTitle className={isHebrew ? "text-right" : "text-left"}>
            {translate("New Task")}
          </DialogTitle>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={handleSubmit(onSubmit)}>
            <div className='flex w-full'>
              <div className='w-full'>
                <FormField
                  control={control}
                  name='tasks_group_id'
                  render={({ field }) => (
                    <FormItem className='mb-4 flex w-full flex-col items-start justify-start space-y-2'>
                      <FormLabel className='capitalize'>{translate("Select Group")}</FormLabel>
                      <FormControl>
                        <Select value={field.value} onValueChange={field.onChange}>
                          <SelectTrigger
                            className={cn(
                              "rounded-lg sm:ml-auto",
                              errors?.tasks_group_id ? "border-red-500 focus:ring-red-500" : "",
                              isHebrew ? "rtl" : "ltr",
                            )}
                          >
                            <SelectValue placeholder={translate("Select Group")} />
                          </SelectTrigger>
                          <SelectContent className={cn("rounded-xl", isHebrew ? "rtl" : "ltr")}>
                            {groupedTasks.map((group, index) => (
                              <SelectItem
                                value={String(group.id)}
                                className='rounded-lg'
                                key={index}
                              >
                                {translate(group?.name)}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={control}
                  name='title'
                  render={({ field }) => (
                    <FormItem className='mb-4 flex flex-col items-start justify-start space-y-2'>
                      <FormLabel className='capitalize'>{translate("New Task")}</FormLabel>
                      <FormControl>
                        <Input
                          placeholder={translate("New Task")}
                          className={cn(
                            `w-full ${errors?.title ? "border-red-500 focus-visible:ring-red-500" : ""}`,
                          )}
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </div>
            <Button className='w-full' loading={addTaskMutation.isPending}>
              {translate("Add Task")}
            </Button>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  )
}

export const NewTask = memo(NewTaskComponent)
